'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, ShoppingBag, FileText, MapPin, ShieldCheck, ChevronRight, Clock } from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';
import type { Order } from '@/data/mockData';

export default function AccountModal() {
  const { isAccountOpen, setIsAccountOpen, setIsTrackingOpen, openInvoice, setActiveView, orders } = useCommerce();
  const [activeTab, setActiveTab] = useState<'orders' | 'addresses' | 'profile'>('orders');

  if (!isAccountOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-2xl bg-[#FFFFFF] rounded-3xl border-2 border-[#E09F55] shadow-2xl p-6 sm:p-8 text-[#171711] my-8"
      >
        <button
          onClick={() => setIsAccountOpen(false)}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white text-[#171711] transition-colors cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center space-x-3 mb-6 font-sans">
          <div className="w-12 h-12 rounded-2xl bg-[#950E09] text-[#F2BC6F] border-2 border-[#E09F55] flex items-center justify-center font-bold text-lg font-display">
            AR
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="font-display text-xl font-bold text-[#171711]">Ananya Raghavan</h3>
              <span className="px-2 py-0.5 rounded-full bg-[#387613]/20 text-[#387613] text-[10px] font-bold uppercase font-sans">
                Verified Customer
              </span>
            </div>
            <p className="text-xs text-[#171711]/70 font-sans">ananya.r@outlook.com • +91 98410 44552</p>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex border-b border-[#DDDAC4] mb-6 space-x-4 text-xs font-bold font-sans">
          <button
            onClick={() => setActiveTab('orders')}
            className={`pb-2.5 transition-all cursor-pointer border-b-2 flex items-center space-x-1.5 ${
              activeTab === 'orders' ? 'border-[#950E09] text-[#950E09]' : 'border-transparent text-[#171711]/60'
            }`}
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            <span>Order History ({orders.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('addresses')}
            className={`pb-2.5 transition-all cursor-pointer border-b-2 flex items-center space-x-1.5 ${
              activeTab === 'addresses' ? 'border-[#950E09] text-[#950E09]' : 'border-transparent text-[#171711]/60'
            }`}
          >
            <MapPin className="w-3.5 h-3.5" />
            <span>Saved Addresses</span>
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-2.5 transition-all cursor-pointer border-b-2 flex items-center space-x-1.5 ${
              activeTab === 'profile' ? 'border-[#950E09] text-[#950E09]' : 'border-transparent text-[#171711]/60'
            }`}
          >
            <User className="w-3.5 h-3.5" />
            <span>Profile &amp; Business GST</span>
          </button>
        </div>

        {/* Tab 1: Orders */}
        {activeTab === 'orders' && (
          <div className="space-y-3 font-sans">
            {orders.length === 0 ? (
              <div className="p-8 text-center bg-neutral-50 rounded-2xl border border-neutral-200 space-y-2">
                <ShoppingBag className="w-8 h-8 text-neutral-400 mx-auto" />
                <p className="text-xs font-bold text-neutral-700">No active orders found yet</p>
                <p className="text-[11px] text-neutral-500 max-w-sm mx-auto">
                  Your placed orders will automatically appear here with real-time status and downloadable GST invoices.
                </p>
              </div>
            ) : (
              orders.map((ord) => (
                <div key={ord.id} className="p-4 rounded-2xl bg-white border border-[#DDDAC4] space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <div className="font-mono font-bold text-[#950E09]">{ord.orderNumber}</div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      ord.orderStatus === 'DELIVERED' ? 'bg-[#387613]/20 text-[#387613]' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {ord.orderStatus}
                    </span>
                  </div>
                  <div className="text-xs text-[#171711]/80 font-sans">
                    {ord.items.map(it => `${it.name} (${it.weight}) × ${it.quantity}`).join(', ')}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs">
                    <span className="font-display font-black text-[#950E09]">₹{ord.totalAmount}</span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setIsAccountOpen(false);
                          setIsTrackingOpen(true);
                        }}
                        className="px-2.5 py-1 rounded-lg bg-[#F7F4D6] hover:bg-[#DDDAC4] border border-[#DDDAC4] text-[11px] font-bold text-[#171711] cursor-pointer"
                      >
                        Track Order
                      </button>
                      <button
                        onClick={() => {
                          setIsAccountOpen(false);
                          openInvoice(ord);
                        }}
                        className="px-2.5 py-1 rounded-lg bg-[#950E09] hover:bg-[#B90505] text-[#F7F4D6] text-[11px] font-bold cursor-pointer"
                      >
                        GST Invoice
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Tab 2: Addresses */}
        {activeTab === 'addresses' && (
          <div className="space-y-3 text-xs">
            <div className="p-4 rounded-2xl bg-white border border-[#E09F55] space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm text-[#171711]">Home (Default)</span>
                <span className="text-[10px] bg-[#387613]/10 text-[#387613] px-2 py-0.5 rounded font-bold">Primary</span>
              </div>
              <p className="text-gray-600">Flat 4B, Heritage Crest, Besant Nagar</p>
              <p className="text-gray-600">Chennai, Tamil Nadu - 600090</p>
            </div>
            <div className="p-4 rounded-2xl bg-white border border-[#DDDAC4] space-y-1">
              <span className="font-bold text-sm text-[#171711]">Office / Corporate Hub</span>
              <p className="text-gray-600">Level 5, DLF Cybercity, Ramapuram</p>
              <p className="text-gray-600">Chennai, Tamil Nadu - 600089</p>
            </div>
          </div>
        )}

        {/* Tab 3: Profile */}
        {activeTab === 'profile' && (
          <div className="space-y-4 text-xs">
            <div className="p-4 rounded-2xl bg-white border border-[#DDDAC4] space-y-3">
              <h4 className="font-bold text-[#950E09]">Business / GST Billing Information</h4>
              <p className="text-gray-600 leading-relaxed">
                Add your company GSTIN to automatically receive Input Tax Credit (ITC) invoices on all retail & wholesale bulk purchases.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="GSTIN Number (optional)"
                  className="p-2.5 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/30 font-mono text-xs"
                />
                <input
                  type="text"
                  placeholder="Company Legal Name"
                  className="p-2.5 rounded-xl border border-[#DDDAC4] bg-[#F7F4D6]/30 text-xs"
                />
              </div>
            </div>
          </div>
        )}

      </motion.div>
    </div>
  );
}
